import React, {
	ReactElement,
	ReactNode,
} from 'react';
import { InputProps } from 'antd/lib/input';
import { Moment } from 'moment';
import Checkbox, { CheckboxProps } from 'antd/lib/checkbox';
import classNames from 'classnames';
import {
	Switch,
	SwitchProps,
} from 'antd';
import { toMoment } from '@shared/utils/date';
import { formatCurrency } from '@shared/utils/currency';
import { Values } from '@shared/utils/objectEnums';
import { Currencies } from '@shared/utils/constants';
import { capitalize } from '@shared/utils/string';
import { formatDate } from '@client/utils/formatDate';
import NumericInput, { NumericInputProps } from '@client/components/NumericInput';
import DatePicker, { DatePickerProps } from '@client/components/DatePicker';
import CurrencyInput, { CurrencyInputProps } from '@client/components/CurrencyInput';
import EditableInput from '@client/components/EditableTableRedux/EditableInput';
import Select, { SelectProps } from '../Select';
import PeriodSelector, {
	PeriodSelectorProps,
	PeriodValue,
} from '../PeriodSelector';
import styles from './styles/EditableCellTableRedux.module.css';

export type EditableFieldReduxProps = (
	& {
		editable: boolean;
		missing?: boolean;
	} &
	EditableFieldReduxPropsByType
);

type CommonEditableFieldProps<Key, ValueType, InputPropsType> = {
	type: Key;
	value: ValueType;
	onChange: (newValue: ValueType) => void;
	inputProps?: Partial<InputPropsType> & { valueOverride?: (row: any) => any };
	renderInput?: RenderInput<ValueType, InputPropsType>;
	renderValue?: RenderValue<ValueType, InputPropsType>;
};

export type EditableFieldReduxPropsByType = (
	| CommonEditableFieldProps<'text', string, InputProps> |
	CommonEditableFieldProps<'number', number | null, NumericInputProps> |
	(CommonEditableFieldProps<'currency', number | null, Omit<CurrencyInputProps, 'currency'>> &
		{ inputProps: { currency: Values<typeof Currencies> } }) |
	CommonEditableFieldProps<'date', Moment | null, DatePickerProps> |
	(CommonEditableFieldProps<'select', string | null, Omit<SelectProps<string>, 'options'>> &
		{ inputProps: { options: SelectProps['options'] } }) |
	CommonEditableFieldProps<'checkbox', boolean, CheckboxProps> |
	CommonEditableFieldProps<'switch', boolean, SwitchProps> |
	CommonEditableFieldProps<'period', PeriodValue, PeriodSelectorProps>
) & {
	transformData?: {
		in?: (value: any, row: any) => any;
		out?: (transformedValues: any, row: any) => any;
	};
};

type EditableFieldsByType = {
	text: {
		renderInput: RenderInput<string, InputProps>;
		renderValue: RenderValue<string, InputProps>;
	};
	number: {
		renderInput: RenderInput<number | null, NumericInputProps>;
		renderValue: RenderValue<number | null, NumericInputProps>;
	};
	currency: {
		renderInput: RenderInput<number | null, Omit<CurrencyInputProps, 'currency'> & { currency: Values<typeof Currencies> }>;
		renderValue: RenderValue<number | null, Omit<CurrencyInputProps, 'currency'> & { currency: Values<typeof Currencies> }>;
	};
	date: {
		renderInput: RenderInput<Moment | null, DatePickerProps>;
		renderValue: RenderValue<Moment | null, DatePickerProps>;
	};
	select: {
		renderInput: RenderInput<string | null, Omit<SelectProps<string>, 'options'> & { options: SelectProps['options'] }>;
		renderValue: RenderValue<string | null, Omit<SelectProps<string>, 'options'> & { options: SelectProps['options'] }>;
	};
	checkbox: {
		renderInput: RenderInput<boolean, CheckboxProps>;
		renderValue: RenderValue<boolean, CheckboxProps>;
	};
	switch: {
		renderInput: RenderInput<boolean, SwitchProps>;
		renderValue: RenderValue<boolean, SwitchProps>;
	};
	period: {
		renderInput: RenderInput<PeriodValue, PeriodSelectorProps>;
		renderValue: RenderValue<PeriodValue, PeriodSelectorProps>;
	};
};

type RenderInput<ValueType, InputPropsType> = (props: {
	value: ValueType;
	onChange: (newValue: ValueType) => Promise<void> | void;
	inputProps: InputPropsType;
	inputPropsWithRow: InputPropsType & {
		valueOverride: any;
	};
	row: any;
}) => ReactNode;

type RenderValue<ValueType, InputPropsType> = (props: {
	value: ValueType;
	inputProps: InputPropsType;
	row: any;
}) => ReactNode;

const editableFieldsByType: EditableFieldsByType = {
	text: {
		renderInput: (props) => (
			<EditableInput
				{...props.inputProps}
				{...props.inputPropsWithRow}
				variant="borderless"
				value={props.value}
				onChange={props.onChange}
			/>
		),
		renderValue: (props) => (
			props.value
		),
	},
	number: {
		renderInput: (props) => (
			<NumericInput
				variant="borderless"
				{...props.inputProps}
				value={props?.inputPropsWithRow?.valueOverride ?? props.value ?? undefined}
				{...props.inputPropsWithRow}
				onChange={props.onChange}
			/>
		),
		renderValue: (props) => (
			props.value
		),
	},
	currency: {
		renderInput: (props) => (
			<CurrencyInput
				variant="borderless"
				{...props.inputProps}
				{...props.inputPropsWithRow}
				value={props.value ?? undefined}
				onChange={props.onChange}
			/>
		),
		renderValue: (props) => {
			if (props.value == null) {
				return null;
			}

			return (
				formatCurrency(props.value, props.inputProps.currency)
			);
		},
	},
	date: {
		renderInput: (props) => (
			<DatePicker
				variant="borderless"
				{...(props.inputProps as any)}
				{...(props.inputPropsWithRow as any)}
				value={props.value}
				onOk={(newValue: any) => {
					if (newValue == null) {
						props.onChange(null);

						return;
					}

					if (Array.isArray(newValue)) {
						throw Error('Return value should not be a range.');
					}

					props.onChange(toMoment(newValue));
				}}
				onChange={(newValue: any) => {
					if (newValue == null) {
						props.onChange(null);

						return;
					}

					if (Array.isArray(newValue)) {
						throw Error('Return value should not be a range.');
					}

					props.onChange(toMoment(newValue));
				}}
			/>
		),
		renderValue: (props) => {
			if (props.value == null) {
				return null;
			}

			return formatDate(props.value);
		},
	},
	select: {
		renderInput: (props) => (
			<Select<string>
				variant="borderless"
				{...props.inputProps}
				{...props.inputPropsWithRow}
				value={props.value ?? undefined}
				options={props.inputProps?.options}
				onChange={(newValue) => props.onChange(newValue)}
				className={classNames(styles.fullWidth, props.inputProps?.className)}
			/>
		),
		renderValue: (props) => (
			props.inputProps?.options?.find((o) => o.value === props.value)?.label ?? null
		),
	},
	checkbox: {
		renderInput: (props) => (
			<Checkbox
				{...props.inputProps}
				{...props.inputPropsWithRow}
				checked={props.value}
				onChange={(newValue) => {
					props.onChange(newValue.target.checked);
				}}
			/>
		),
		renderValue: (props) => (
			<Checkbox
				checked={props.value}
				disabled
			/>
		),
	},
	period: {
		renderInput: (props) => (
			<PeriodSelector
				{...(props.inputProps || {})}
				value={props?.inputProps?.value!}
				onChange={(newValue) => props.onChange(newValue)}
			/>
		),
		renderValue: (props) => `${capitalize(String(props.value[2]))} ${props.value[0]} ${capitalize(props.value[1])}`,
	},
	switch: {
		renderInput: (props) => (
			<Switch
				{...props.inputProps}
				{...props.inputPropsWithRow}
				checked={props.value}
				onChange={(newValue) => {
					props.onChange(newValue);
				}}
			/>
		),
		renderValue: (props) => (
			<Switch
				checked={props.value}
				disabled
			/>
		),
	},
};

const EditableFieldRedux = (
	props: EditableFieldReduxProps,
): ReactElement => {
	const {
		type,
		editable,
		renderInput: renderInputOverride,
		renderValue: renderValueOverride,
	} = props;

	const { renderInput, renderValue } = editableFieldsByType[type];

	if (editable) {
		return (
			<>
				{(renderInputOverride ?? renderInput)(props as any)}
			</>
		);
	}

	return <>{(renderValueOverride ?? renderValue)(props as any)}</>;
};

export default EditableFieldRedux;
